import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import firebase from "firebase/app";
import "firebase/auth";
import store, { awaitLiffInitialized } from "@/store";
import { getRole } from "@/api/auth";
import { getStudent } from "@/api/student";
import liff from "@line/liff/dist/lib";
import { pageViewLogEvent, setUserIdToAnalytics } from "@/api/analytics";
import { getDocIdsOfStudentRef } from "@/entities/student";
import { getSchoolConfig } from "@/api/school";
import dayjs from "dayjs";

let authUnsubscribe: firebase.Unsubscribe | null = null;

function updateIsInLiff() {
  store.commit("SET_IS_IN_LIFF", liff.isInClient());
}

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "home",
    component: () => import("../views/Home.vue"),
    beforeEnter(_, __, next) {
      store.dispatch("updateStudent");
      store.dispatch("getLearnings");
      // 複数回の入室を防ぐためのパラメータのうち、昨日までに使われたものを削除
      Object.keys(localStorage).forEach(key => {
        if (key.indexOf("room_") !== 0) return;
        const [yyyy, mm, dd] = key
          .split("_")[1]
          .split("-")
          .map(_ => +_);
        if (isNaN(yyyy) || isNaN(mm) || isNaN(dd)) return;
        const d = dayjs()
          .year(yyyy)
          .month(mm - 1)
          .date(dd);
        if (d.isBefore(dayjs().startOf("day"))) {
          localStorage.removeItem(key);
        }
      });
      next();
    },
    meta: { pageType: "home" }
  },
  {
    path: "/login",
    name: "login",
    component: () => import("../views/Login.vue"),
    meta: { pageType: "other" }
  },
  {
    path: "/auth/reader",
    name: "auth_reader",
    component: () => import("../views/AuthReader.vue"),
    meta: { pageType: "other" }
  },
  {
    path: "/analyze/future",
    name: "analyze_future",
    beforeEnter(_, __, next) {
      if (!store.state.isInLiff) {
        next("/");
        return;
      }
      store.commit("SET_LEARNING_PAGE_TYPE", "future");
      next();
    },
    component: () => import("../views/Learning.vue"),
    meta: { pageType: "other" }
  },
  // {
  //   path: "/badge",
  //   name: "badge_list",
  //   component: () => import("../views/BadgeList.vue"),
  //   meta: { pageType: "other" }
  // },
  {
    path: "/learning",
    name: "learning_history",
    beforeEnter(_, __, next) {
      if (!store.state.isInLiff) {
        next("/");
        return;
      }
      store.commit("SET_LEARNING_PAGE_TYPE", "history");
      next();
    },
    component: () => import("../views/Learning.vue"),
    meta: { pageType: "other" }
  },
  {
    path: "/learning_start",
    name: "learning_start",
    component: () => import("../views/LearningStart.vue"),
    meta: { pageType: "other" }
  },
  {
    path: "/learning/:learningId",
    name: "learning",
    component: () => import("../views/LearningDetail.vue"),
    meta: { pageType: "other" }
  },
  {
    path: "/learning/:learningId/reflection",
    name: "learning_reflection",
    component: () => import("../views/ReflectionForm.vue"),
    meta: { pageType: "other" }
  },
  {
    path: "/reflection",
    name: "reflection",
    component: () => import("../views/ReflectionLatest.vue"),
    meta: { pageType: "other" }
  },
  // {
  //   path: "/message",
  //   name: "message",
  //   component: () => import("../views/Message.vue"),
  //   meta: { pageType: "message" }
  // },
  {
    path: "/setting",
    name: "setting",
    component: () => import("../views/Setting.vue"),
    meta: { pageType: "setting" }
  },
  // {
  //   path: "/notification",
  //   name: "notification",
  //   component: () => import("../views/Notification.vue"),
  //   meta: { pageType: "other" }
  // },
  {
    path: "/room/form",
    name: "room_enter_form",
    component: () => import("../views/EnterForm.vue"),
    meta: { pageType: "other" }
  },
  {
    path: "/room/reservation",
    name: "room_reservation",
    component: () => import("../views/Reservation.vue"),
    meta: { pageType: "other" }
  },
  {
    path: "/waiting",
    name: "waiting",
    component: () => import("../views/WaitingView.vue"),
    meta: { pageType: "other" }
  }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior: (to, from, savedPosition) => {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  }
});

router.beforeEach(async to => {
  await awaitLiffInitialized(store);

  pageViewLogEvent(to.fullPath, to.path, to.name as string);

  updateIsInLiff();

  if (authUnsubscribe !== null) {
    authUnsubscribe();
  }
  authUnsubscribe = firebase.auth().onAuthStateChanged(async function(user) {
    if (!user) {
      if (to.name === "login" || to.name === "auth_reader") {
        return;
      } else {
        store.commit("SET_REDIRECT", to.path);
        router.replace("/login");
        return;
      }
    }

    setUserIdToAnalytics(user.uid);

    if (!store.state.role) {
      store.commit("SET_LOADING", true);
      const role = await getRole(user.uid);
      if (!role) {
        store.commit("SET_LOADING", false);
        router.replace("/login");
        return;
      }
      store.commit("SET_ROLE", role);
      const docIds = getDocIdsOfStudentRef(role.data.ref);
      const [student, schoolConfig] = await Promise.all([
        getStudent(role.data.ref),
        getSchoolConfig(docIds[0])
      ]);

      store.commit("SET_LOADING", false);
      if (!student || !schoolConfig) {
        alert("生徒情報の取得に失敗しました。再度ログインし直してください");
        await store.dispatch("signOut");
        router.replace("/login");
        return;
      } else if (
        (student.data.recessTime && student.data.recessTime > 0) ||
        (schoolConfig.data.recessTime && schoolConfig.data.recessTime > 0)
      ) {
        alert("このアカウントは現在休会中であるため、利用することができません");
        await store.dispatch("signOut");
        router.replace("/login");
        return;
      }
      store.commit("SET_STUDENT", student);
      const classroomRef = student.ref.parent.parent;
      classroomRef && store.commit("SET_CLASSROOM_DOC_ID", classroomRef.id);
      const schoolRef = classroomRef?.parent.parent;
      schoolRef && store.commit("SET_SCHOOL_DOC_ID", schoolRef.id);
      store.dispatch("getLearnings");
      store.dispatch("getGroups");
      store.dispatch("getRoomReservations");
    }
    store.commit(
      "SET_IS_ANDROID",
      navigator.userAgent.toLowerCase().indexOf("android") !== -1
    );

    if (to.name === "login" || to.name === "auth_reader") {
      router.replace("/");
      return;
    }
  });
});

export default router;
